import React, { createContext, useContext, useEffect, useState } from 'react';

type ProfilePreference = 'teams' | 'solo' | null;

interface Preferences {
  defaultProfile: ProfilePreference;
  hasSeenTeams: boolean;
}

interface PreferencesContextType {
  preferences: Preferences;
  resetPreferences: () => void;
  updatePreferences: (updates: Partial<Preferences>) => void;
}

const defaultPreferences: Preferences = {
  defaultProfile: null,
  hasSeenTeams: false,
};

const PreferencesContext =
  createContext<PreferencesContextType | undefined>(undefined);

export const PreferencesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [preferences, setPreferences] =
    useState<Preferences>(defaultPreferences);

  // Load preferences from localStorage on mount
  useEffect(() => {
    const storedPreferences = localStorage.getItem('preferences');
    if (storedPreferences) {
      try {
        const parsedPreferences = JSON.parse(storedPreferences);
        // Ensure backward compatibility with old preferences structure
        setPreferences({
          ...defaultPreferences,
          ...parsedPreferences,
          hasSeenTeams: parsedPreferences.hasSeenTeams ?? false,
        });
      } catch (error) {
        console.error('Failed to parse stored preferences:', error);
      }
    }
  }, []);

  // Save preferences to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('preferences', JSON.stringify(preferences));
  }, [preferences]);

  const updatePreferences = (updates: Partial<Preferences>) => {
    setPreferences((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  const resetPreferences = () => {
    setPreferences(defaultPreferences);
  };

  return (
    <PreferencesContext.Provider
      value={{ preferences, updatePreferences, resetPreferences }}
    >
      {children}
    </PreferencesContext.Provider>
  );
};

export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (context === undefined) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
};
